import React, { useState } from "react";
import { useData } from "@/context/DataContext";
import { PageTitle, Fab, StickyTotal, RowActions, EmptyState } from "@/components/shared";
import DataDialog from "@/components/DataDialog";
import { formatRupiah, parseNumber } from "@/lib/format";
import { isBought } from "@/lib/compute";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ExternalLink } from "lucide-react";
import { toast } from "sonner";

const KATEGORI = ["Perangkat Sholat", "Make up dan Skin Care", "Peralatan Mandi", "Pakaian Dalam", "Pakaian Luar", "Lain-lain"];
const PREVIEW = "https://images.unsplash.com/photo-1562249003-f713a2af1096?crop=entropy&cs=srgb&fm=jpg&q=85&w=120";

function Thumb({ link }) {
  const favicon = link ? `https://www.google.com/s2/favicons?domain=${encodeURIComponent(link)}&sz=64` : null;
  return (
    <div className="h-10 w-10 overflow-hidden rounded-md border border-border bg-muted">
      <img src={favicon || PREVIEW} alt="preview" className="h-full w-full object-cover"
        onError={(e) => { e.currentTarget.src = PREVIEW; }} />
    </div>
  );
}

export default function Seserahan() {
  const { data, create, update, remove } = useData();
  const items = data.seserahan || [];
  const payments = data.pembayaran || [];
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const total = items.reduce((s, i) => s + parseNumber(i.estimasi), 0);

  const fields = [
    { name: "kebutuhan", label: "Kebutuhan", type: "text", required: true },
    { name: "kategori", label: "Kategori", type: "select", options: KATEGORI, required: true },
    { name: "link_checkout", label: "Link Check Out (opsional)", type: "text" },
    { name: "estimasi", label: "Estimasi Harga", type: "currency", required: true },
    { name: "keterangan", label: "Keterangan", type: "textarea" },
  ];

  const onSave = async (payload) => {
    if (editing) { await update("seserahan", editing.id, payload); toast.success("Seserahan diperbarui"); }
    else { await create("seserahan", payload); toast.success("Seserahan ditambahkan"); }
  };

  return (
    <div>
      <PageTitle title="List Seserahan" subtitle="Kelompok per kategori, status dari pembayaran" />
      <StickyTotal testId="seserahan-total">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">Total Estimasi Seserahan</p>
        <p className="font-heading text-3xl font-semibold" data-testid="seserahan-total-value">{formatRupiah(total)}</p>
      </StickyTotal>

      {items.length === 0 && <EmptyState />}
      {KATEGORI.map((kat) => {
        const rows = items.filter((i) => i.kategori === kat);
        if (rows.length === 0) return null;
        return (
          <div key={kat} className="mb-8">
            <div className="mb-2 flex items-center gap-3">
              <span className="h-px flex-1 bg-border" />
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">{kat}</span>
              <span className="h-px flex-1 bg-border" />
            </div>
            <div className="rounded-xl border border-border">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Kebutuhan</TableHead><TableHead>Link</TableHead><TableHead>Gambar</TableHead>
                  <TableHead className="text-right">Harga</TableHead><TableHead>Keterangan</TableHead>
                  <TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {rows.map((i) => {
                    const bought = isBought(i, payments, "seserahan");
                    return (
                      <TableRow key={i.id} data-testid={`row-${i.id}`} className={bought ? "bg-green-500/15" : ""}>
                        <TableCell className="font-medium">{i.kebutuhan}</TableCell>
                        <TableCell>{i.link_checkout ? <a href={i.link_checkout} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary underline"><ExternalLink className="h-3 w-3" />Link</a> : "-"}</TableCell>
                        <TableCell><Thumb link={i.link_checkout} /></TableCell>
                        <TableCell className="text-right">{formatRupiah(i.estimasi)}</TableCell>
                        <TableCell className="max-w-[160px] truncate text-muted-foreground">{i.keterangan || "-"}</TableCell>
                        <TableCell>{bought ? <Badge className="bg-green-600">Sudah dibeli</Badge> : <Badge variant="outline">Belum</Badge>}</TableCell>
                        <TableCell><RowActions id={i.id} onEdit={() => { setEditing(i); setOpen(true); }} onDelete={() => remove("seserahan", i.id)} /></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
        );
      })}

      <Fab testId="fab-seserahan" onClick={() => { setEditing(null); setOpen(true); }} />
      <DataDialog open={open} onOpenChange={setOpen} title={editing ? "Edit Seserahan" : "Tambah Seserahan"} fields={fields} initial={editing} onSave={onSave} />
    </div>
  );
}
